import type { RecordLikeProtocol } from '@lighthouse/core'

const CREATED_TIME_FIELD_ID = 'createdTime'

/**
 * 获取记录的 sortId 值
 * @returns [value, hasValue] - sortId值和是否有有效值
 */
export function getSortValue(record: RecordLikeProtocol, sortFieldId?: string): [number, boolean] {
    if (!sortFieldId) {
        return [0, false]
    }
    const value = record.content[sortFieldId]?.value
    if (value === null || value === undefined || value === '') {
        return [0, false]
    }
    if (typeof value === 'number') {
        return [value, true]
    }
    const num = Number(value)
    return Number.isNaN(num) ? [0, false] : [num, true]
}

/**
 * 获取记录的创建时间
 */
export function getCreatedTime(record: RecordLikeProtocol): number {
    const createdTime = record.content[CREATED_TIME_FIELD_ID]?.value
    if (typeof createdTime === 'number') {
        return createdTime
    }
    if (typeof createdTime === 'string') {
        const timestamp = Date.parse(createdTime)
        if (!Number.isNaN(timestamp)) {
            return timestamp
        }
    }
    return 0
}

/**
 * 获取记录的有效排序值（统一数值空间）
 *
 * 规则：
 * - 有 sortId：返回 sortId
 * - 无 sortId：返回 -创建时间（负数，使创建时间降序变为升序）
 */
export function getEffectiveSortValue(record: RecordLikeProtocol, sortFieldId?: string): number {
    const [sortValue, hasSortId] = getSortValue(record, sortFieldId)

    if (hasSortId) {
        return sortValue
    }

    const createdTime = getCreatedTime(record)
    return -createdTime
}

/**
 * 计算被拖动记录的新排序值（只更新1条记录）
 *
 * 核心逻辑：
 * - 使用"有效排序值"（sortId 或 -创建时间）确定前后节点位置
 * - 新 sortId = (前节点有效值 + 后节点有效值) / 2
 *
 * @param siblings - 兄弟节点列表（不包含被拖动的记录）
 * @param insertIndex - 插入位置索引
 * @param sortFieldId - 排序字段ID
 * @returns 新的排序值
 */
export function calculateNewSortValue(
    siblings: { id: string; record: RecordLikeProtocol }[],
    insertIndex: number,
    sortFieldId: string
): number {
    const prevNode = siblings[insertIndex - 1]
    const nextNode = siblings[insertIndex]

    const prevValue = prevNode ? getEffectiveSortValue(prevNode.record, sortFieldId) : null
    const nextValue = nextNode ? getEffectiveSortValue(nextNode.record, sortFieldId) : null

    if (prevValue === null && nextValue === null) {
        return 1
    }

    if (prevValue === null) {
        return nextValue! - 1
    }

    if (nextValue === null) {
        return prevValue + 1
    }

    return (prevValue + nextValue) / 2
}
